import { useEffect, useState } from 'react';
import { Landmark, LogOut } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Banks } from '@/components/Banks';
import { ExposureReview } from '@/components/ExposureReview';
import { Login } from '@/components/Login';
import { Unauthorized, listTransactions, logout } from '@/lib/api';
import { readParams, writeParams } from '@/lib/url';

type View = { mode: 'banks' } | { mode: 'txns'; source: string | null; title: string };

function txnsTitle(source: string | null): string {
  return source ? `${source.toUpperCase()} transactions` : 'All transactions';
}

function viewFromUrl(): View {
  const params = readParams();
  if (params.get('view') !== 'transactions') return { mode: 'banks' };
  const source = params.get('bank');
  return { mode: 'txns', source, title: txnsTitle(source) };
}

/**
 * Accounting app shell (row 2). Finance signs in with the shared passphrase, then
 * reviews transactions and includes/excludes them for Foundry. The full labelling
 * grid, cross-bank balances, and per-user auth are row 5 — this is the minimal
 * decision surface. Styling reuses the Foundry-vendored design system.
 */
export function App() {
  // null = probing the session, true = signed in, false = show login.
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [view, setView] = useState<View>(viewFromUrl);
  // Bumped on Back/Forward so the transactions view remounts and re-reads its filters from the URL.
  const [historyStep, setHistoryStep] = useState(0);

  useEffect(() => {
    const onPop = () => {
      setView(viewFromUrl());
      setHistoryStep((n) => n + 1);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function openTransactions(source: string | null, account?: number) {
    writeParams({ view: 'transactions', bank: source, account: account ? String(account) : null }, 'push');
    setView({ mode: 'txns', source, title: txnsTitle(source) });
  }

  function openBanks() {
    writeParams({}, 'push');
    setView({ mode: 'banks' });
  }

  useEffect(() => {
    listTransactions({ exposure: 'undecided', limit: 1 })
      .then(() => setAuthed(true))
      .catch((e) => setAuthed(e instanceof Unauthorized ? false : true));
  }, []);

  async function signOut() {
    try {
      await logout();
      setAuthed(false); // only drop to login once the server has cleared the cookie
    } catch {
      // Logout failed (cookie may still be live) — stay signed in rather than show
      // a misleading logged-out screen.
    }
  }

  if (authed === null) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!authed) {
    return <Login onSuccess={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <header className="sticky top-0 z-10 border-b bg-primary text-primary-foreground">
        <div className="mx-auto flex h-14 w-full max-w-5xl items-center gap-3 px-4">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-secondary text-primary">
            <Landmark className="size-5" aria-hidden />
          </span>
          <div className="flex min-w-0 flex-col leading-tight">
            <span className="truncate text-sm font-semibold sm:text-base">Metycle Accounting</span>
            <span className="truncate text-xs text-primary-foreground/70">Finance operations</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={signOut}
            className="ml-auto gap-2 text-primary-foreground hover:bg-primary/80 hover:text-primary-foreground"
          >
            <LogOut className="size-4" aria-hidden />
            <span className="hidden sm:inline">Sign out</span>
          </Button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl px-4 py-6 sm:py-8">
        {view.mode === 'banks' ? (
          <Banks
            onOpen={openTransactions}
            onAuthError={() => setAuthed(false)}
          />
        ) : (
          <ExposureReview
            source={view.source}
            title={view.title}
            key={`${view.source ?? 'all'}:${historyStep}`}
            onBack={openBanks}
            onAuthError={() => setAuthed(false)}
          />
        )}
      </main>

      <footer className="mx-auto w-full max-w-5xl px-4 pb-8 pt-2">
        <p className="text-xs text-muted-foreground">Metycle Accounting — internal preview.</p>
      </footer>
    </div>
  );
}
